import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaCalendarCheck,
  FaChartLine,
  FaCheckCircle,
  FaPhoneAlt,
  FaPowerOff,
  FaStar,
  FaUsers,
} from 'react-icons/fa';
import api from '../lib/api';
import './CSS/PlatformDashboard.css';

export default function PlatformDashboard() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [tenantRes, demoRes] = await Promise.all([
        api.get('/api/tenants', authHeaders),
        api.get('/api/demo-requests/platform', authHeaders),
      ]);

      setTenants(Array.isArray(tenantRes.data) ? tenantRes.data : tenantRes.data?.data || []);
      setDemoRequests(Array.isArray(demoRes.data) ? demoRes.data : demoRes.data?.data || []);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load platform dashboard');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalTenants = tenants.length;
    const activeTenants = tenants.filter((t) => Number(t.is_active) === 1).length;
    const inactiveTenants = totalTenants - activeTenants;

    const totalLeads = demoRequests.length;
    const newLeads = demoRequests.filter((d) => d.status === 'new').length;
    const scheduledLeads = demoRequests.filter((d) => d.status === 'demo_scheduled').length;
    const convertedLeads = demoRequests.filter((d) => d.status === 'converted').length;
    const contactedLeads = demoRequests.filter((d) => ['assigned', 'contacted', 'followup'].includes(d.status)).length;

    const activationRate = totalTenants ? Math.round((activeTenants / totalTenants) * 100) : 0;
    const conversionRate = totalLeads ? Math.round((convertedLeads / totalLeads) * 100) : 0;
    const scheduledRate = totalLeads ? Math.round((scheduledLeads / totalLeads) * 100) : 0;

    return {
      totalTenants,
      activeTenants,
      inactiveTenants,
      totalLeads,
      newLeads,
      scheduledLeads,
      convertedLeads,
      contactedLeads,
      activationRate,
      conversionRate,
      scheduledRate,
    };
  }, [tenants, demoRequests]);

  const recentTenants = useMemo(() => tenants.slice(0, 5), [tenants]);
  const recentLeads = useMemo(() => demoRequests.slice(0, 5), [demoRequests]);
  const inactiveTenantList = useMemo(
    () => tenants.filter((tenant) => Number(tenant.is_active) !== 1).slice(0, 4),
    [tenants]
  );
  const priorityLeads = useMemo(
    () => demoRequests.filter((lead) => ['new', 'demo_scheduled', 'followup'].includes(lead.status)).slice(0, 5),
    [demoRequests]
  );

  const topCities = useMemo(() => {
    const counts = new Map();
    tenants.forEach((tenant) => {
      const city = String(tenant.city || '').trim();
      if (!city) return;
      counts.set(city, (counts.get(city) || 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([city, count]) => ({ city, count }));
  }, [tenants]);

  const overviewCards = [
    {
      title: 'Tenants',
      value: stats.totalTenants,
      hint: `${stats.activeTenants} active | ${stats.inactiveTenants} inactive`,
      tone: 'blue',
      icon: FaBuilding,
    },
    {
      title: 'Lead Pipeline',
      value: stats.totalLeads,
      hint: `${stats.newLeads} new | ${stats.contactedLeads} in progress`,
      tone: 'violet',
      icon: FaPhoneAlt,
    },
    {
      title: 'Activation Rate',
      value: `${stats.activationRate}%`,
      hint: 'Share of tenants currently live',
      tone: 'green',
      icon: FaCheckCircle,
    },
    {
      title: 'Lead Conversion',
      value: `${stats.conversionRate}%`,
      hint: `${stats.convertedLeads} leads converted to tenants`,
      tone: 'amber',
      icon: FaChartLine,
    },
  ];

  return (
    <div className="platform-dashboard-page">
      <section className="platform-hero-card">
        <div className="platform-hero-copy">
          <div className="platform-chip">Platform Intelligence Layer</div>
          <h1>Super admin workspace for platform growth and tenant operations.</h1>
          <p>
            Keep onboarding, lead follow-up, and tenant health in one command view so the team can move
            faster without jumping across multiple screens.
          </p>

          <div className="platform-hero-actions">
            <button className="platform-hero-btn primary" onClick={() => navigate('/platform/tenants')}>
              Open Tenant Control
            </button>
            <button className="platform-hero-btn secondary" onClick={() => navigate('/platform/demo-leads')}>
              Open Demo Pipeline
            </button>
          </div>
        </div>

        <div className="platform-hero-side">
          <div className="platform-hero-panel">
            <div className="platform-hero-panel-kicker">Today's focus</div>
            <div className="platform-hero-panel-title">
              {loading ? 'Syncing overview' : `${stats.newLeads} new leads to respond`}
            </div>
            <p>
              {loading
                ? 'Pulling latest platform activity.'
                : `${stats.scheduledLeads} demos are scheduled and ${stats.inactiveTenants} tenants need attention.`}
            </p>
          </div>

          <div className="platform-hero-metrics">
            <div className="platform-inline-metric">
              <span>Scheduled Demo Rate</span>
              <strong>{stats.scheduledRate}%</strong>
            </div>
            <div className="platform-inline-metric">
              <span>Converted Leads</span>
              <strong>{stats.convertedLeads}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="platform-overview-grid">
        {overviewCards.map((card) => (
          <OverviewCard key={card.title} {...card} />
        ))}
      </section>

      <section className="platform-focus-grid">
        <div className="platform-surface">
          <div className="platform-surface-head">
            <div>
              <div className="platform-section-kicker">Priority Queue</div>
              <h3>Leads that need attention</h3>
            </div>
            <button className="platform-link-btn" onClick={() => navigate('/platform/demo-leads')}>
              Open Pipeline
              <FaArrowRight />
            </button>
          </div>

          {loading ? (
            <div className="platform-empty-state">Loading lead queue...</div>
          ) : priorityLeads.length === 0 ? (
            <div className="platform-empty-state">No high-priority leads right now.</div>
          ) : (
            <div className="platform-feed-list">
              {priorityLeads.map((lead) => (
                <div className="platform-feed-item" key={lead.id}>
                  <div className="platform-feed-icon tone-violet">
                    <FaStar />
                  </div>
                  <div className="platform-feed-copy">
                    <strong>{lead.company_name || 'Unnamed lead'}</strong>
                    <span>{lead.contact_person || '-'} | {lead.phone || '-'}</span>
                  </div>
                  <div className={`platform-status-pill lead-${lead.status || 'new'}`}>
                    {formatStatus(lead.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="platform-surface">
          <div className="platform-surface-head">
            <div>
              <div className="platform-section-kicker">Tenant Watchlist</div>
              <h3>Inactive or pending stations</h3>
            </div>
            <button className="platform-link-btn" onClick={() => navigate('/platform/tenants')}>
              Open Tenants
              <FaArrowRight />
            </button>
          </div>

          {loading ? (
            <div className="platform-empty-state">Loading tenant watchlist...</div>
          ) : inactiveTenantList.length === 0 ? (
            <div className="platform-empty-state">All tenants are currently active.</div>
          ) : (
            <div className="platform-feed-list">
              {inactiveTenantList.map((tenant) => (
                <div className="platform-feed-item" key={tenant.id}>
                  <div className="platform-feed-icon tone-amber">
                    <FaPowerOff />
                  </div>
                  <div className="platform-feed-copy">
                    <strong>{tenant.name || 'Unnamed tenant'}</strong>
                    <span>{tenant.code || '-'} | {tenant.city || 'No city'}</span>
                  </div>
                  <div className="platform-status-pill neutral">
                    Inactive
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="platform-board-grid">
        <div className="platform-surface">
          <div className="platform-surface-head">
            <div>
              <div className="platform-section-kicker">Tenant Feed</div>
              <h3>Recently onboarded tenants</h3>
            </div>
            <button className="platform-link-btn" onClick={() => navigate('/platform/tenants')}>
              View All
              <FaArrowRight />
            </button>
          </div>

          {loading ? (
            <div className="platform-empty-state">Loading tenants...</div>
          ) : recentTenants.length === 0 ? (
            <div className="platform-empty-state">No tenants onboarded yet.</div>
          ) : (
            <div className="platform-list-board">
              {recentTenants.map((tenant) => (
                <div className="platform-list-row" key={tenant.id}>
                  <div>
                    <div className="platform-list-title">{tenant.name || '-'}</div>
                    <div className="platform-list-sub">{tenant.code || '-'} | {tenant.company || 'No company'}</div>
                  </div>
                  <div className={`platform-status-pill ${Number(tenant.is_active) === 1 ? 'positive' : 'neutral'}`}>
                    {Number(tenant.is_active) === 1 ? 'Active' : 'Inactive'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="platform-surface">
          <div className="platform-surface-head">
            <div>
              <div className="platform-section-kicker">Lead Feed</div>
              <h3>Recently captured demo requests</h3>
            </div>
            <button className="platform-link-btn" onClick={() => navigate('/platform/demo-leads')}>
              Open Pipeline
              <FaArrowRight />
            </button>
          </div>

          {loading ? (
            <div className="platform-empty-state">Loading leads...</div>
          ) : recentLeads.length === 0 ? (
            <div className="platform-empty-state">No demo requests received yet.</div>
          ) : (
            <div className="platform-list-board">
              {recentLeads.map((lead) => (
                <div className="platform-list-row" key={lead.id}>
                  <div>
                    <div className="platform-list-title">{lead.company_name || '-'}</div>
                    <div className="platform-list-sub">{lead.contact_person || '-'} | {lead.phone || '-'}</div>
                  </div>
                  <div className={`platform-status-pill lead-${lead.status || 'new'}`}>
                    {formatStatus(lead.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="platform-mini-grid">
        <div className="platform-surface">
          <div className="platform-surface-head compact">
            <div>
              <div className="platform-section-kicker">Coverage</div>
              <h3>Top tenant cities</h3>
            </div>
          </div>
          {topCities.length ? (
            <div className="platform-city-list">
              {topCities.map((row) => (
                <div className="platform-city-row" key={row.city}>
                  <span>{row.city}</span>
                  <strong>{row.count}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div className="platform-empty-state">City coverage appears once tenants are added.</div>
          )}
        </div>

        <div className="platform-surface">
          <div className="platform-surface-head compact">
            <div>
              <div className="platform-section-kicker">Quick Paths</div>
              <h3>Most common super admin actions</h3>
            </div>
          </div>

          <div className="platform-quick-actions">
            <button className="platform-quick-action" onClick={() => navigate('/platform/tenants')}>
              <FaBuilding />
              <div>
                <strong>Onboard Tenant</strong>
                <span>Create a new station and admin account</span>
              </div>
            </button>

            <button className="platform-quick-action" onClick={() => navigate('/platform/demo-leads')}>
              <FaCalendarCheck />
              <div>
                <strong>Schedule Demo</strong>
                <span>Move fresh requests into the next action step</span>
              </div>
            </button>

            <button className="platform-quick-action" onClick={fetchDashboard}>
              <FaUsers />
              <div>
                <strong>Refresh Platform</strong>
                <span>Pull latest tenant and lead activity</span>
              </div>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function OverviewCard({ title, value, hint, tone, icon: Icon }) {
  return (
    <div className={`platform-overview-card tone-${tone}`}>
      <div className="platform-overview-icon">
        <Icon />
      </div>
      <div className="platform-overview-copy">
        <span>{title}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </div>
  );
}

function formatStatus(status) {
  return String(status || 'new')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
